import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { flattenToRows, tableFromArray } from '../../helpers/entities';

const MAX_ROWS = 30;

const isUrl = value => typeof value === 'string' && /^https?:\/\//i.test(value.trim());
const isImageUrl = value => isUrl(value) && /\.(jpe?g|png|gif|webp|svg|avif)([?#].*)?$/i.test(value.trim());

const renderValue = value => {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  const str = String(value).trim();
  if (isImageUrl(str)) {
    return <img src={str} alt="" className="h-20 w-20 rounded-md object-cover" onError={e => { e.currentTarget.style.display = 'none'; }} />;
  }
  if (isUrl(str)) {
    return <a href={str} target="_blank" rel="noopener noreferrer" className="break-all text-space underline">{str}</a>;
  }
  return str;
};

const isObject = value => value && typeof value === 'object' && !Array.isArray(value);

function ProseList({ data }) {
  const items = Array.isArray(data) ? data : [data];
  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        if (typeof item === 'string') return <p key={index} className="text-sm leading-relaxed text-ink">{item}</p>;
        if (isObject(item)) {
          const title = item?.title || item?.headline || item?.name;
          const body = item?.body || item?.description || item?.content || item?.text || item?.summary;
          return (
            <div key={index} className="rounded-lg border border-white/10 bg-white/5 p-3">
              {title && <h4 className="mb-1 text-sm font-semibold text-ink">{title}</h4>}
              {body && <p className="whitespace-pre-wrap text-xs leading-relaxed text-mute">{body}</p>}
            </div>
          );
        }
        return <p key={index} className="text-sm leading-relaxed text-ink">{String(item)}</p>;
      })}
    </div>
  );
}

export function ToolResultBody({ result }) {
  const { kind, data } = result;
  const isObjectData = isObject(data);
  const isObjectList = Array.isArray(data) && data.length && data.every(isObject);
  const isScalarList = Array.isArray(data) && data.length && data.every(item => !isObject(item));
  const isProse = kind === 'prose' || typeof data === 'string';

  if (isProse) return <ProseList data={data} />;

  if (isObjectList && kind !== 'keyvalue') {
    const { columns, rows } = tableFromArray(data);
    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col} className="border border-white/10 bg-white/5 px-2 py-1 text-left font-semibold text-ink">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, MAX_ROWS).map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="border border-white/10 px-2 py-1 align-top text-mute">{renderValue(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > MAX_ROWS && <p className="mt-1 text-[11px] text-mute">Showing {MAX_ROWS} of {rows.length}</p>}
      </div>
    );
  }

  if (isObjectData) {
    const rows = flattenToRows(data);
    return (
      <dl className="space-y-1.5">
        {rows.map(({ key, label: rowLabel, value }) => (
          <div key={key} className="flex items-start justify-between gap-4 border-b border-white/10 pb-1.5">
            <dt className="text-xs font-semibold uppercase tracking-wide text-mute">{rowLabel}</dt>
            <dd className="break-words text-right text-sm text-ink">{renderValue(value)}</dd>
          </div>
        ))}
      </dl>
    );
  }

  if (isScalarList) {
    return (
      <ul className="list-inside list-disc space-y-1 text-sm text-ink">
        {data.slice(0, MAX_ROWS).map((item, index) => <li key={index}>{renderValue(item)}</li>)}
      </ul>
    );
  }

  return (
    <pre className="max-h-[40vh] overflow-auto rounded-lg border border-white/10 bg-black/30 p-3 text-[11px] leading-relaxed text-mute">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

const ToolResult = ({ result }) => {
  const [open, setOpen] = useState(false);
  const { label } = result;
  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-white/5">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-ink transition-colors hover:bg-white/5">
        <span>{label}</span>
        <ChevronRight className={`h-4 w-4 shrink-0 text-mute transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="border-t border-white/10 p-3"><ToolResultBody result={result} /></div>}
    </div>
  );
};

export default ToolResult;
